import {
	Component,
	ComponentInterface,
	Element,
	h,
	Host,
	Listen,
	Prop,
	State,
} from '@stencil/core';
import ScrollingHelper from '../_helpers/scrolling';
import { ComponentWithScrollingInterface } from '../_interfaces/component-with-scrolling.interface';
import { scrollTo } from 'scroll-js';
import debounce from 'lodash/debounce';
import { Breakpoint, getCurrentBreakpoint } from '../_helpers/breakpoints';
import { calculateChildTagName } from '../_helpers/prefixes';

const OffsetWindowScrollPositionMobileTablet = 57; // Due to sticky nav menu
const OffsetWindowScrollPositionDesktop = 0;
const MeasureOffsetsDebounceMs = 250;
const ActivateAnchorDebounceMs = 100;
const ScrollingFromClickExtraTimeMs = 200; // Wait a little longer after scrolling stops before re-activating scroll listener events

@Component({
	tag: 'ds-anchor-links',
})
export class AnchorLinks
	implements ComponentInterface, ComponentWithScrollingInterface
{
	@Element() el: HTMLElement;
	@Prop() bgColor = 'yellow';
	@State() sticky = false;
	@State() focusedIndex = 0;
	@State() selectedIndex: number = null;
	@State() selectedItem: HTMLElement = null; // TODO: think about removing this
	@State() contentHeight: number = null;
	@State() showArrowLeft = false;
	@State() showArrowRight = true;

	sections: Array<HTMLElement> = [];
	sectionsOffsetTop: Array<number>; // The number of pixels from the top of the page where a section should be viewable from
	scrollableItems: Array<HTMLElement> = [];
	scrollingContainer: HTMLElement;
	scrollingHelper;

	// While scrolling the window down as the result of a click we need to prevent the scroll listener interfering
	scrollingFromClick = false;

	debouncedMeasureOffsets;
	debouncedActivateAnchor;

	sectionsOffsetTimer;
	sectionsOffsetCalcCount = 0;

	@Listen('scroll', { target: 'window' })
	windowScrollHandler() {
		const shouldBeSticky = this.shouldBeSticky();

		if (!this.scrollingFromClick) {
			this.debouncedActivateAnchor();
			if (!this.sticky && shouldBeSticky) {
				// Make sure that there is no debounce delay in activating first link
				this.activateLink(0);
			}
		}
		this.sticky = shouldBeSticky;
		return;
	}

	@Listen('resize', { target: 'window' })
	windowResizeHandler() {
		this.debouncedMeasureOffsets();
		this.debouncedActivateAnchor();
		return this.scrollingHelper.windowResizeHandler();
	}

	componentWillLoad(): void {
		this.scrollingHelper = new ScrollingHelper(this, {
			horizontalLabelOffset: 32,
		});
		this.scrollableItems = Array.from(
			this.el.querySelectorAll<HTMLLIElement>('li')
		);
		const anchorLinks = this.scrollableItems.map((element) =>
			element.querySelector('a')
		);
		const sectionNames = [];
		anchorLinks.forEach((anchor, i) => {
			if (anchor) {
				anchor.innerHTML = '<span>' + anchor.innerHTML + '</span>';
				anchor.addEventListener('click', (event) =>
					this.handleItemClick(event, i)
				);
				const anchorLinkName = anchor.getAttribute('name');
				if (anchorLinkName) {
					sectionNames.push(anchorLinkName);
				} else {
					console.error('Anchor link name not found.');
				}
			}
		});
		this.sections = this.getSections(sectionNames);
		this.measureSectionsOffsetTop();
		this.setupHiddenTabFocusElements();
		this.debouncedMeasureOffsets = debounce(
			this.measureSectionsOffsetTop,
			MeasureOffsetsDebounceMs
		);
		this.debouncedActivateAnchor = debounce(
			this.activateAnchorForSectionCurrentlyInView,
			ActivateAnchorDebounceMs
		);
		this.sticky = this.shouldBeSticky();
		this.activateLinkByHashValue(window.location.hash.replace('#', ''));

		// Every second for 5 seconds after page load, recalculate the positions of the sections in case they change
		this.sectionsOffsetTimer = setInterval(
			() => this.recalculateSectionsOffsetForFiveSeconds(),
			1000
		);
	}

	componentDidLoad(): void {
		this.showArrowRight = this.scrollingHelper.shouldRightArrowBeShown();
	}

	/**
	 * Get the sections that anchor links are linking to
	 */
	getSections(sectionNames: Array<string>): Array<HTMLElement> {
		return sectionNames
			.map((name) => {
				let section = document.querySelector<HTMLElement>(
					`[data-component-display-name="${name}"]`
				);

				if (!section) {
					section = document.querySelector<HTMLElement>(
						`[data-componentname="${name}"]`
					);

					if (!section) {
						console.error(`Section ${name} not found in Anchor Links.`);
					}
				}

				return section;
			})
			.filter((section) => section); // Remove any null values due to elements not being found
	}

	/**
	 * Measure the distance from the top for the beginnings of all sections, so that when scrolling we can know which section
	 * is currently in view.
	 *
	 * Note: this.sections must be populated before calling this function.
	 */
	measureSectionsOffsetTop = () => {
		this.sectionsOffsetTop = [];
		this.sections.forEach((section) =>
			this.sectionsOffsetTop.push(this.getSectionPosition(section))
		);
	};

	recalculateSectionsOffsetForFiveSeconds() {
		this.sectionsOffsetCalcCount++;
		if (this.sectionsOffsetCalcCount >= 5) {
			clearInterval(this.sectionsOffsetTimer);
		}
		this.debouncedMeasureOffsets();
	}

	getSectionPosition(section: HTMLElement) {
		const basePosition =
			section.getBoundingClientRect().top +
			window.scrollY -
			this.el.offsetHeight;

		if (
			[Breakpoint.XS, Breakpoint.SM, Breakpoint.MD].includes(
				getCurrentBreakpoint()
			)
		) {
			return basePosition - OffsetWindowScrollPositionMobileTablet;
		} else {
			return basePosition - OffsetWindowScrollPositionDesktop;
		}
	}

	setupHiddenTabFocusElements() {
		this.sections.forEach((section) => {
			const hiddenButton = document.createElement('button');
			hiddenButton.classList.add('ds-receive-focus');
			hiddenButton.classList.add('d-none');

			section.insertBefore(hiddenButton, section.firstChild);
		});
	}

	shouldBeSticky() {
		let offset = 0;
		if (
			[Breakpoint.XS, Breakpoint.SM, Breakpoint.MD].includes(
				getCurrentBreakpoint()
			)
		) {
			offset = OffsetWindowScrollPositionMobileTablet;
		}
		return this.scrollingHelper.hasWindowScrolledPastElement(this.el, offset);
	}

	activateAnchorForSectionCurrentlyInView = () => {
		if (this.scrollingFromClick) {
			return;
		}
		let index = 0;
		this.sectionsOffsetTop.forEach((offset, i) => {
			if (offset < window.scrollY) {
				index = i;
			}
		});

		this.activateLink(index);
	};

	/**
	 * Handle clicking on an individual item
	 */
	handleItemClick(e: MouseEvent, index: number): void {
		e.preventDefault();

		if (e.currentTarget) {
			const componentKey = (e.currentTarget as HTMLAnchorElement).getAttribute(
				'name'
			);
			this.scrollWindowToComponent(componentKey);
			window.location.hash = encodeURIComponent(
				componentKey.replace(/ /g, '-')
			);
		}

		this.activateLink(index);
	}

	activateLink(index: number) {
		this.scrollableItems.forEach((item, i) => {
			const anchor = item.querySelector<HTMLAnchorElement>('a');
			if (index === i) {
				anchor.classList.add('ds-active');
				anchor.tabIndex = 0;
			} else {
				anchor.classList.remove('ds-active');
				anchor.tabIndex = -1;
			}
		});

		this.selectedIndex = index;
		if (this.scrollingContainer) {
			this.scrollingHelper.animateSelectedItemIntoView();
		}
	}

	activateLinkByHashValue(hashValue: string): void {
		this.scrollableItems.forEach((item, i) => {
			const anchor = item.querySelector<HTMLAnchorElement>('a');
			const componentKey = anchor.getAttribute('name');
			if (encodeURIComponent(componentKey.replace(/ /g, '-')) === hashValue) {
				this.scrollWindowToComponent(componentKey);
				this.activateLink(i);
			}
		});
	}

	scrollWindowToComponent(componentKey: string) {
		let targetElement = document.querySelector<HTMLElement>(
			`[data-component-display-name="${componentKey}"]`
		);
		if (!targetElement) {
			targetElement = document.querySelector<HTMLElement>(
				`[data-componentname="${componentKey}"]`
			);
		}
		if (targetElement) {
			const newWindowScrollPosition = this.getSectionPosition(targetElement);
			this.scrollingFromClick = true;
			scrollTo(window, {
				behavior: 'smooth',
				top: newWindowScrollPosition,
			}).then(() => {
				// Once the window has completely stopped scrolling, allow the scroll listener to affect the active link again
				setTimeout(() => {
					this.scrollingFromClick = false;
				}, ScrollingFromClickExtraTimeMs);
			});
		}
	}

	handleKeyDown(e: KeyboardEvent): void {
		const componentKey = (e.target as HTMLAnchorElement).getAttribute('name');

		switch (e.code) {
			case 'Home':
				this.scrollingHelper.focusOnLabel(0, 'a');
				e.preventDefault();
				break;
			case 'End':
				this.scrollingHelper.focusOnLabel(this.scrollableItems.length - 1, 'a');
				e.preventDefault();
				break;
			case 'ArrowLeft':
				if (this.focusedIndex <= 0) {
					this.scrollingHelper.focusOnLabel(
						this.scrollableItems.length - 1,
						'a'
					);
				} else {
					this.scrollingHelper.focusOnLabel(this.focusedIndex - 1, 'a');
				}
				e.preventDefault();
				break;
			case 'ArrowRight':
				if (this.focusedIndex >= this.scrollableItems.length - 1) {
					this.scrollingHelper.focusOnLabel(0, 'a');
				} else {
					this.scrollingHelper.focusOnLabel(this.focusedIndex + 1, 'a');
				}
				e.preventDefault();
				break;
			case 'Tab':
				if (!e.shiftKey) {
					this.shiftFocusToActiveSection();
				}
				break;
			case 'Space':
			case 'Enter':
				this.selectedIndex = this.focusedIndex;
				this.activateLink(this.focusedIndex);
				this.scrollWindowToComponent(componentKey);
				e.preventDefault();
				break;
		}
	}

	shiftFocusToActiveSection() {
		// Shift focus to a hidden tabbable element in the active section and then invoke the tab key again to go to next focusable item
		const focusableButton =
			this.sections[this.selectedIndex || 0].querySelector<HTMLButtonElement>(
				'.ds-receive-focus'
			);
		if (focusableButton) {
			focusableButton.classList.remove('d-none');
			focusableButton.focus();
			focusableButton.classList.add('d-none');
		}
	}

	getHeightStyle(): { height?: string } {
		const style: { height?: string } = {};
		if (this.contentHeight) {
			style.height = this.contentHeight.toString() + 'px';
		}
		return style;
	}

	render() {
		const arrowButtonColor = this.bgColor;
		const ArrowButton = calculateChildTagName(this.el, 'ds-arrow-button');

		return (
			<Host>
				<section
					class={`container-fluid ds-scrolling-arrows-wrapper ds-bg-ishares-${
						this.bgColor
					} ${this.sticky ? 'ds-sticky' : ''}`}
				>
					<div class="row">
						<div class="col ds-col">
							<nav
								class="ds-scrolling-items"
								ref={(el) => (this.scrollingContainer = el as HTMLElement)}
								onScroll={() => this.scrollingHelper.handleScroll()}
								aria-label="In Page Navigation Bar"
								onKeyDown={(e) => this.handleKeyDown(e)}
							>
								<ArrowButton
									direction="left"
									button-color={arrowButtonColor}
									class={{ 'd-none': !this.showArrowLeft }}
									onClick={() => this.scrollingHelper.handleLeftArrowClick()}
								/>
								<ArrowButton
									direction="right"
									button-color={arrowButtonColor}
									class={{ 'd-none': !this.showArrowRight }}
									onClick={() => this.scrollingHelper.handleRightArrowClick()}
								/>

								<slot />
							</nav>
						</div>
					</div>
				</section>
			</Host>
		);
	}
}
